import { watch } from 'vue'

/**
 * Closes the deposit modal when the route *path* moves away from the one it
 * was opened on. The game-page Deposit controls navigate to /wallet before
 * opening the modal (#864), so browser Back would otherwise land on `/` with
 * the modal still open over the game, where session replay is paused again.
 *
 * Lives in app.vue rather than in DepositModal: the modal is an on-first-open
 * async chunk (mountOnceOpened), so for the first open of a session there is
 * no component to watch anything during the 0.3–21 s the chunk takes — a Back
 * in that window would leave the modal to mount over the game. app.vue is
 * alive at open time, so the opening path is captured when the store flips.
 *
 * The router's live `currentRoute`, not `useRoute()`: outside NuxtPage that
 * one is Nuxt's `_route`, which only syncs after the new page has rendered —
 * after the deposit entry has already opened the modal on /wallet — so a plain
 * "path changed" watcher would close every modal opened after the first.
 * `openedOnPath` is captured synchronously (`flush: 'sync'`) for the same
 * reason. Path only: the character switcher stamps `?unlockReturn=` in the
 * query right after opening the modal (#751), and that must not close it.
 */
export function useDepositModalRouteClose(): void {
  const router = useRouter()
  const uiStore = useUIStore()

  let openedOnPath: string | null = null
  watch(() => uiStore.showDepositModal, (open) => {
    openedOnPath = open ? router.currentRoute.value.path : null
  }, { immediate: true, flush: 'sync' })

  watch(() => router.currentRoute.value.path, (path) => {
    if (uiStore.showDepositModal && openedOnPath !== null && path !== openedOnPath) {
      uiStore.closeDepositModal()
    }
  })
}
